import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Modal from "react-modal";
import { Link } from "react-router-dom";
import HoleByHoleGrid, { GridRow } from "./HoleByHoleGrid";
import ScorecardUploadButton from "./ScorecardUploadButton";
import ScorecardPhoto from "./ScorecardPhoto";
import { CellValue } from "./CellEditor";
import { useIsMobile } from "../hooks/useMediaQuery";
import { inferPlayedHoles, rowTotals } from "../utils/holeScores";
import { OPEN_STATUSES, UploadStatus, statusLabel } from "../utils/scorecardApi";
import "../css/scorecard.css";

Modal.setAppElement("#root");

const API_BASE_URL = process.env.REACT_APP_API_URL;

interface ViewRoundByHoleProps {
  isOpen: boolean;
  onClose: () => void;
  roundId: number | null;
  getAccessTokenSilently: () => Promise<string>;
  /** Called after hole scores are saved (so the page can refresh chips). */
  onChanged?: () => void;
}

interface PlayerScore {
  player_id: number;
  firstName: string;
  lastName: string;
  team: string | null;
  playerScore: number | null;
  scores: (number | null)[];
  putts?: (number | null)[];
  penalties?: (number | null)[];
  holesUploadId?: number | null;
}

interface ScorecardRef {
  upload_id: number;
  status: UploadStatus;
  uploader_name: string | null;
  is_mine: boolean;
  created_at: string;
  committed_at: string | null;
}

interface RoundDetails {
  course_name: string;
  round_date: string;
  course_teebox: string;
  number_of_holes: number;
  course_teebox_par: number;
  course_teebox_rating: number;
  course_teebox_slope: number;
  course_teebox_yards: number;
  holes: { holeNumber: number; par: number | null; yards: number | null }[];
  players: PlayerScore[];
  scorecards?: ScorecardRef[];
}

interface RowDraft { strokes: (number | null)[]; putts: (number | null)[]; penalties: (number | null)[] }

const blank18 = () => new Array(18).fill(null) as (number | null)[];
const norm18 = (list?: (number | null)[]) => {
  const out = blank18();
  (list || []).slice(0, 18).forEach((v, i) => { out[i] = v == null ? null : Number(v); });
  return out;
};
const sameRow = (a: RowDraft, b: RowDraft) =>
  (['strokes', 'putts', 'penalties'] as const).every((k) => a[k].every((v, i) => v === b[k][i]));

const formatDate = (d: string) => {
  if (!d) return "—";
  const date = new Date(d.length === 10 ? `${d}T12:00:00` : d);
  return isNaN(date.getTime()) ? d : date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
};

/**
 * A round's hole-by-hole scores: read first, Edit to change them, putts and
 * penalty strokes on demand, and the scorecard photos behind them — including
 * "📷 Upload scorecard" to fill the holes in from a photo of the paper card.
 */
const ViewRoundByHole: React.FC<ViewRoundByHoleProps> = ({ isOpen, onClose, roundId, getAccessTokenSilently, onChanged }) => {
  const isMobile = useIsMobile();
  const [data, setData] = useState<RoundDetails | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saved, setSaved] = useState<Record<number, RowDraft>>({});
  const [draft, setDraft] = useState<Record<number, RowDraft>>({});
  const [editing, setEditing] = useState(false);
  const [showExtras, setShowExtras] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [photoFor, setPhotoFor] = useState<number | null>(null);
  // The Rounds page hands us a new token getter on every render; reloading on
  // that would wipe unsaved edits, so hold it in a ref.
  const tokenRef = useRef(getAccessTokenSilently);
  tokenRef.current = getAccessTokenSilently;
  const getToken = useCallback(() => tokenRef.current(), []);

  const load = useCallback(async () => {
    if (!roundId) return;
    setLoadError(null);
    try {
      const token = await getToken();
      const response = await fetch(`${API_BASE_URL}/getRoundDetails?roundId=${roundId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("Couldn't load this round's scores.");
      const d: RoundDetails = await response.json();
      const rows: Record<number, RowDraft> = {};
      for (const p of d.players) rows[p.player_id] = { strokes: norm18(p.scores), putts: norm18(p.putts), penalties: norm18(p.penalties) };
      setData(d);
      setSaved(rows);
      setDraft(rows);
      setShowExtras(Object.values(rows).some((r) => r.putts.some((v) => v != null) || r.penalties.some((v) => v != null && v > 0)));
    } catch (err: any) {
      setLoadError(err?.message || "Couldn't load this round's scores.");
    }
  }, [roundId, getToken]);

  useEffect(() => {
    if (!isOpen || !roundId) return;
    setData(null);
    setEditing(false);
    setMessage(null);
    setPhotoFor(null);
    load();
  }, [isOpen, roundId, load]);

  const pars = useMemo(() => (data ? norm18(data.holes.map((h) => h.par)) : blank18()), [data]);
  const playedHoles = useMemo(
    () => inferPlayedHoles(data?.number_of_holes, Object.values(saved).map((r) => r.strokes)),
    [data, saved],
  );
  const uploadsById = useMemo(() => new Map((data?.scorecards || []).map((u) => [u.upload_id, u])), [data]);
  const changedIds = useMemo(
    () => Object.keys(draft).map(Number).filter((id) => saved[id] && !sameRow(draft[id], saved[id])),
    [draft, saved],
  );

  if (!isOpen) return null;

  const rows: GridRow[] = (data?.players || []).map((p) => {
    const r = draft[p.player_id] || { strokes: blank18(), putts: blank18(), penalties: blank18() };
    const t = rowTotals(r.strokes, playedHoles);
    const src = p.holesUploadId ? uploadsById.get(p.holesUploadId) : null;
    const warning = t.complete && p.playerScore != null && t.total !== Number(p.playerScore)
      ? `holes add up to ${t.total}; the round's gross is ${p.playerScore}`
      : null;
    return {
      key: String(p.player_id),
      first: p.firstName,
      last: p.lastName,
      strokes: r.strokes,
      putts: r.putts,
      penalties: r.penalties,
      warning,
      subtitle: src ? (
        <button type="button" className="sc-btn sc-btn--ghost sc-btn--sm" style={{ padding: 0, fontSize: "inherit" }} onClick={() => setPhotoFor(src.upload_id)}>
          📷 from {src.uploader_name ? `${src.uploader_name.split(" ")[0]}'s` : "a"} scorecard
        </button>
      ) : undefined,
    };
  });

  const onCellChange = (rowKey: string, hole: number, v: CellValue) => {
    const id = Number(rowKey);
    setDraft((prev) => {
      const r = prev[id];
      const next = { strokes: [...r.strokes], putts: [...r.putts], penalties: [...r.penalties] };
      next.strokes[hole - 1] = v.strokes;
      next.putts[hole - 1] = v.putts;
      next.penalties[hole - 1] = v.penalties;
      return { ...prev, [id]: next };
    });
    setMessage(null);
  };

  const save = async () => {
    if (!roundId || !changedIds.length) { setEditing(false); return; }
    setSaving(true);
    setMessage(null);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/updateRoundScores`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          roundId,
          players: changedIds.map((id) => ({ player_id: id, scores: draft[id].strokes, putts: draft[id].putts, penalties: draft[id].penalties })),
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || "Couldn't save the scores.");
      setSaved(draft);
      setEditing(false);
      setMessage({ ok: true, text: `Saved ${changedIds.length} player${changedIds.length === 1 ? "" : "s"}.` });
      onChanged?.();
    } catch (err: any) {
      setMessage({ ok: false, text: err?.message || "Couldn't save the scores." });
    } finally {
      setSaving(false);
    }
  };

  const cancelEdit = () => {
    if (changedIds.length && !window.confirm("Discard your changes?")) return;
    setDraft(saved);
    setEditing(false);
    setMessage(null);
  };

  const close = () => {
    if (editing && changedIds.length && !window.confirm("You have unsaved changes. Close anyway?")) return;
    onClose();
  };

  const scorecards = (data?.scorecards || []).filter((u) => u.status !== "discarded");
  const openCards = scorecards.filter((u) => OPEN_STATUSES.includes(u.status));

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={close}
      shouldCloseOnOverlayClick={false}
      contentLabel="Hole-by-hole scores"
      style={{
        overlay: { zIndex: 1050, backgroundColor: "rgba(15, 23, 42, 0.5)" },
        content: isMobile
          ? { inset: 0, borderRadius: 0, padding: "14px 12px", border: 0 }
          : { inset: "32px", borderRadius: 14, padding: "20px 22px", maxWidth: 1240, margin: "0 auto" },
      }}
    >
      <div className="sc-modal-head">
        <div>
          <h2>{data?.course_name || "Hole by hole"}</h2>
          {data && (
            <div className="sc-facts">
              <span className="sc-fact">{formatDate(data.round_date)}</span>
              {data.course_teebox && <span className="sc-fact">{data.course_teebox} tees</span>}
              <span className="sc-fact">{playedHoles.length} holes</span>
              {data.course_teebox_par != null && <span className="sc-fact">Par <b>{data.course_teebox_par}</b></span>}
              {data.course_teebox_rating != null && <span className="sc-fact">Rating <b>{data.course_teebox_rating}</b> / Slope <b>{data.course_teebox_slope}</b></span>}
              {data.course_teebox_yards != null && <span className="sc-fact">{data.course_teebox_yards} yds</span>}
            </div>
          )}
        </div>
        <button type="button" className="sc-btn sc-btn--ghost" aria-label="Close" onClick={close} style={{ fontSize: 22, padding: "2px 10px" }}>×</button>
      </div>

      {loadError && <p className="sc-error">{loadError}</p>}
      {!data && !loadError && <p className="sc-note">Loading…</p>}

      {data && (
        <>
          <div className="sc-cards">
            {openCards.map((u) => (
              <div key={u.upload_id} className="sc-cards-row">
                <span className={`sc-chip-status sc-chip-status--${u.status === "ready" ? "ready" : u.status === "reading" || u.status === "agent_working" ? "reading" : "review"}`}>
                  📷 {statusLabel(u.status)}
                </span>
                <span className="sc-note">Scorecard from {u.uploader_name || "someone"}</span>
                {u.is_mine && <Link className="sc-btn sc-btn--sm sc-btn--primary" to={`/scorecard-review/${u.upload_id}`}>Review</Link>}
              </div>
            ))}
            <div className="sc-cards-row">
              <span className="sc-note" style={{ flex: 1 }}>
                {scorecards.some((u) => u.status === "committed")
                  ? `${scorecards.filter((u) => u.status === "committed").length} scorecard photo${scorecards.filter((u) => u.status === "committed").length === 1 ? "" : "s"} saved to this round.`
                  : "Have the paper card? Upload a photo and the holes are filled in for you."}
              </span>
              {scorecards.filter((u) => u.status === "committed").map((u) => (
                <button key={u.upload_id} type="button" className="sc-btn sc-btn--sm" onClick={() => setPhotoFor(photoFor === u.upload_id ? null : u.upload_id)}>
                  {photoFor === u.upload_id ? "Hide photo" : `View ${u.uploader_name ? `${u.uploader_name.split(" ")[0]}'s` : ""} photo`}
                </button>
              ))}
              {roundId && <ScorecardUploadButton roundId={roundId} getToken={getToken} label="📷 Upload scorecard" className="sc-btn sc-btn--sm sc-btn--primary" />}
            </div>
            {photoFor && <ScorecardPhoto uploadId={photoFor} getToken={getToken} height={isMobile ? 260 : 380} />}
          </div>

          <div className="sc-toolbar">
            {!editing ? (
              <button type="button" className="sc-btn sc-btn--sm" onClick={() => { setEditing(true); setMessage(null); }}>✏️ Edit scores</button>
            ) : (
              <span className="sc-note">Tap a hole to change it.</span>
            )}
            <label className="sc-note" style={{ display: "inline-flex", alignItems: "center", gap: 6, marginLeft: "auto" }}>
              <input type="checkbox" checked={showExtras} onChange={(e) => setShowExtras(e.target.checked)} />
              Putts &amp; penalties
            </label>
          </div>

          <HoleByHoleGrid
            pars={pars}
            playedHoles={playedHoles}
            rows={rows}
            editable={editing}
            showExtras={showExtras}
            onCellChange={onCellChange}
          />

          <div className="sc-legend" style={{ marginTop: 8 }}>
            <span><span className="sc-score sc-score--birdie">3</span> birdie</span>
            <span><span className="sc-score sc-score--eagle">2</span> eagle</span>
            <span><span className="sc-score sc-score--bogey">5</span> bogey</span>
            <span><span className="sc-score sc-score--double">6</span> double+</span>
            {showExtras && <span>2p = putts · <span style={{ color: "#b45309" }}>•</span> = penalty stroke</span>}
          </div>

          <div className="sc-footer">
            {message && <span className={message.ok ? "sc-ok" : "sc-error"} role="status">{message.text}</span>}
            <span className="grow" />
            {editing ? (
              <>
                <button type="button" className="sc-btn" onClick={cancelEdit} disabled={saving}>Cancel</button>
                <button type="button" className="sc-btn sc-btn--primary" onClick={save} disabled={saving || !changedIds.length}>
                  {saving ? "Saving…" : changedIds.length ? `Save ${changedIds.length} player${changedIds.length === 1 ? "" : "s"}` : "No changes"}
                </button>
              </>
            ) : (
              <button type="button" className="sc-btn" onClick={close}>Close</button>
            )}
          </div>
        </>
      )}
    </Modal>
  );
};

export default ViewRoundByHole;
